import { useEffect, useRef, useState } from "react";
import AgentMark from "./AgentMark";
import Composer from "./Composer";
import MessageBubble from "./MessageBubble";
import QuickActionPills from "./QuickActionPills";

export default function ConversationView({ messages, isLoading, isSending, statusText, error, onSend, onDismissError }) {
    const scrollRef = useRef(null);
    const composerRef = useRef(null);
    const dockRef = useRef(null);
    const [dockHeight, setDockHeight] = useState(120);
    const pinnedRef = useRef(true);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, isSending]);

    // Keep the last message clear of the floating composer as it grows (long text, attachments, errors).
    useEffect(() => {
        const el = dockRef.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(() => {
            const sc = scrollRef.current;
            // Remember whether the reader was at the bottom before the composer changed size.
            if (sc) pinnedRef.current = sc.scrollHeight - sc.scrollTop - sc.clientHeight < 200;
            setDockHeight(el.offsetHeight + 40);
        });
        observer.observe(el);
        return () => observer.disconnect();
    });

    // A taller composer shrinks the visible thread; stay at the bottom if the reader was there.
    useEffect(() => {
        if (pinnedRef.current && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [dockHeight]);

    const lastMessage = messages[messages.length - 1];
    const canChoose = !isSending && lastMessage?.options?.length > 0;

    const composer = (
        <Composer ref={composerRef} disabled={isSending} error={error} onSend={onSend} onDismissError={onDismissError} />
    );

    if (isLoading) {
        return (
            <div className="flex h-full min-h-0 flex-1 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-100 border-t-primary-500" />
            </div>
        );
    }

    if (messages.length === 0 && !isSending) {
        return (
            <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center px-4 sm:px-6">
                <div className="flex w-full max-w-xl flex-col items-center text-center">
                    <span className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50">
                        <AgentMark className="h-8 w-8" />
                    </span>
                    <h2 className="font-newsreader text-4xl font-medium tracking-tight text-secondary sm:text-5xl">
                        What do you want to make?
                    </h2>
                    <p className="mt-3 font-Mulish text-base text-graphite">
                        Try a garment on, change a photo, or generate a look.
                    </p>
                    <div className="mt-7 w-full">{composer}</div>
                    <QuickActionPills
                        className="mt-4 w-full justify-center"
                        onPick={(prompt) => composerRef.current?.setText(prompt)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="relative h-full min-h-0 flex-1">
            <div ref={scrollRef} className="absolute inset-0 overflow-y-auto px-4 pt-8 sm:px-8" style={{ paddingBottom: dockHeight + 24 }}>
                <div className="mx-auto flex max-w-3xl flex-col gap-7">
                    {messages.map((message) => (
                        <MessageBubble
                            key={message.id}
                            message={message}
                            canChoose={canChoose && message === lastMessage}
                            onChoose={(option) => onSend(option, [])}
                        />
                    ))}
                    {isSending && (
                        <div className="agent-in flex items-center gap-3" role="status" aria-live="polite">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-secondary shadow-[0_0_0_1px_var(--hairline)]">
                                <AgentMark className="agent-working h-[18px] w-[18px]" />
                            </span>
                            <span className="font-Mulish text-sm text-graphite">{statusText || "Thinking…"}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* The composer floats over the thread. The fade lets messages dissolve into it, so there is no seam. */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-white from-60% to-white/0 px-4 pb-4 pt-10 sm:px-8">
                <div ref={dockRef} className="pointer-events-auto mx-auto max-w-3xl">
                    <Composer variant="dock" ref={composerRef} disabled={isSending} error={error} onSend={onSend} onDismissError={onDismissError} />
                </div>
            </div>
        </div>
    );
}
